'use client';
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

import {
  DeliveryType,
  type EstimateIntercityOrderRequestBody,
  type EstimateNewOrderRequestBody,
} from '@ahm/api-wrappers-core';
import { useAuth } from '@ahm/react-auth';
import {
  ActionIcon,
  Box,
  Button,
  FocusTrap,
  Grid,
  rem,
  TextInput,
} from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ModalName } from '../..';
import classes from './promo-item.module.css';
import { useEstimatedFeePartner } from '@/api/order';
import SeachIcon from '@/assets/icons/search-icon.svg';
import XCircle from '@/assets/icons/xcircle-icon.svg';
import { ROUTES } from '@/config/router';
import { useEstimateIntercityOrder } from '@/features/booking/api/intercity';
import { useEstimateOrder } from '@/features/booking/api/order/use-estimate-order';
import {
  useMutationStatus,
  usePromoCode,
} from '@/features/booking/hooks/use-booking-promotion';
import { useBookingRequestBodies } from '@/features/booking/hooks/use-booking-request';
import { INVALID_PROMOTION_CREATE } from '@/lib/constants';
import { checkPartnerFromToken } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type InputPromoProps = {
  selectedVoucherIds: string[];
};

export default function InputPromo({ selectedVoucherIds }: InputPromoProps) {
  const tBooking = useTranslations('booking');
  const router = useRouter();
  const pathname = usePathname();
  const { session } = useAuth();
  const token = session?.token;

  // Use nuqs for URL state management
  const [, setUrlPromoIds] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  const { estimatedData, selectedService, errorPromo, setEstimatedData } =
    useBookingStore((state) => state);
  const [isTyping, setIsTyping] = useState(false);
  const { paramPromo } = usePromoCode();
  const { mutationStatusNationwide, mutationStatusOther, loading } =
    useMutationStatus();

  const { estimateRequestBody } = useBookingRequestBodies(pathname, null);

  const isPartner = token ? checkPartnerFromToken(token) : false;

  function handleInputPromoEstimateSuccess(res: any) {
    if (!res) return;
    const data = selectedService
      ? res.find((item) => item.service_id === selectedService._id)
      : null;

    setEstimatedData(data);
    if (!data?.data?.promo_error_message) {
      const promoCodeIds = data?.data?.discount_details
        ? data.data.discount_details
            .filter((item) => item.is_valid)
            .map((item) => item.promo_code_id)
        : [];
      void setUrlPromoIds(promoCodeIds);
    }
  }

  const estimatedFeePartner = useEstimatedFeePartner({
    onSuccess: handleInputPromoEstimateSuccess,
    onError: () => void setUrlPromoIds([]),
  });

  const estimateOrder = useEstimateOrder({
    onSuccess: handleInputPromoEstimateSuccess,
    onError: () => void setUrlPromoIds([]),
  });

  const onEstimateOrder = isPartner
    ? estimatedFeePartner.mutate
    : estimateOrder.mutate;

  const { mutate: onEstimateInterCityOrder } = useEstimateIntercityOrder({
    onSuccess: handleInputPromoEstimateSuccess,
    onError: () => router.replace(pathname),
  });

  const form = useForm<{ code: string }>({
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (!paramPromo) form.setValue('code', '');
  }, [paramPromo, form]);

  const onSubmit = useCallback(
    (value: { code: string }) => {
      const trimmedCode = value.code.trim();
      if (selectedService?.delivery_type === DeliveryType.Nationwide) {
        if (mutationStatusNationwide.length === 0)
          closeModal(ModalName.Promotion);
      } else {
        if (mutationStatusOther.length === 0) closeModal(ModalName.Promotion);
      }

      if (trimmedCode) {
        const body = {
          ...estimateRequestBody,
          promo_code_ids: [...selectedVoucherIds, trimmedCode],
        };

        if (pathname === ROUTES.nationwide) {
          onEstimateInterCityOrder(
            body as unknown as EstimateIntercityOrderRequestBody
          );
        } else {
          onEstimateOrder(body as EstimateNewOrderRequestBody);
        }
      }
    },
    [
      selectedService?.delivery_type,
      mutationStatusNationwide.length,
      mutationStatusOther.length,
      estimateRequestBody,
      selectedVoucherIds,
      pathname,
      onEstimateInterCityOrder,
      onEstimateOrder,
    ]
  );

  const errorPromoCode = useMemo(() => {
    if (estimatedData?.error?.code === INVALID_PROMOTION_CREATE) {
      setIsTyping(false);
      form.setError('code', { message: estimatedData.error.description });
    } else if (errorPromo) {
      setIsTyping(false);
      form.setError('code', { message: errorPromo });
    } else if (estimatedData?.data?.promo_error_message && !errorPromo) {
      setIsTyping(false);
      form.setError('code', {
        message: estimatedData.data.promo_error_message,
      });
    }

    return (
      errorPromo ||
      estimatedData?.data?.promo_error_message ||
      (estimatedData?.error?.code === INVALID_PROMOTION_CREATE
        ? estimatedData?.error?.description
        : null)
    );
  }, [estimatedData, form, errorPromo]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const uppercaseValue = e.target.value.toUpperCase();
      form.setValue('code', uppercaseValue);
      form.setError('code', { message: '' });
      setIsTyping(true);
    },
    [form]
  );

  return (
    <Box component="form" onSubmit={form.handleSubmit(onSubmit)}>
      <Grid gutter={rem(8)} styles={{ root: { height: '100%' } }}>
        <Grid.Col span="auto">
          <Controller
            name="code"
            control={form.control}
            render={({ field }) => (
              <FocusTrap active>
                <TextInput
                  {...field}
                  radius={rem(8)}
                  autoComplete="off"
                  type="text"
                  h="auto"
                  onChange={handleInputChange}
                  size="sm"
                  placeholder={tBooking('promo.enter_promo_code')}
                  error={
                    errorPromoCode &&
                    !isTyping &&
                    !loading &&
                    form.getValues('code')
                      ? errorPromoCode
                      : ''
                  }
                  classNames={{
                    input: classes.inputPromo,
                  }}
                  styles={{
                    error: {
                      visibility:
                        isTyping || loading || !form.getValues('code')
                          ? 'hidden'
                          : 'visible',
                    },
                  }}
                  leftSection={
                    <SeachIcon
                      size={rem(24)}
                      c="neutral.7"
                      variant="transparent"
                      p={8}
                    />
                  }
                  rightSection={
                    <>
                      {field.value ? (
                        <ActionIcon
                          aria-label="Clear promo code"
                          variant="transparent"
                          onClick={() => form.setValue('code', '')}
                        >
                          <XCircle />
                        </ActionIcon>
                      ) : null}
                    </>
                  }
                />
              </FocusTrap>
            )}
          />
        </Grid.Col>
        <Grid.Col span="content">
          <Button
            type="submit"
            disabled={
              !form.watch('code').trim() ||
              (errorPromoCode &&
                !isTyping &&
                !loading &&
                form.getValues('code').trim())
                ? true
                : false
            }
            loading={loading}
            h={rem(40)}
            miw={rem(100)}
            className={classes.promoButtonUse}
            radius={rem(8)}
          >
            {tBooking('promo.button_use_promo_code')}
          </Button>
        </Grid.Col>
      </Grid>
    </Box>
  );
}
